import React from 'react';
import PropTypes from 'prop-types';
import useStyles from '@front10/helpers/dist/UseStyles';
import { isSSR } from '@front10/utils';
import withCommonEMContainer from '../_commons/CommonEMContainer';

//-------------------------------------
// Basic components here
//-------------------------------------
import Row from '../../elements/Row';
import Col from '../../elements/Col';
import Disclaimer from '../../others/Disclaimer';
import Skeleton from '../../others/Skeleton/SkeletonFaresCarouselBackgroundImageMobile';
import Loading from '../../others/Loading';
import NoFaresBehavior from '../../others/NoFaresBehavior/NoFaresBehavior';
import NoFeaturedPopularFaresAlert from '../../alerts/NoFeaturedPopularFaresAlert';
import NextDestinationAlert from '../../alerts/NextDestinationAlert';
import ContainerErrorAlert from '../../alerts/ContainerErrorAlert';
import Carousel from '../../controls/Carousel';
import FareItemCard from '../../items/FareItemCard';

//-------------------------------------
// Every mundo integration
//-------------------------------------
import { getAirports } from '../../../_integrations/everymundo/airports';
import { getHistogramsByMonths } from '../../../_integrations/everymundo/histogram';

//-------------------------------------
// Commons functions
//-------------------------------------
import { useFilters, OriginDestinationLogic } from '../_commons/filters';
import InlineFilters from '../../controls/InlineFilters/InlineFiltersMobile';
import { getResultsLength } from '../_commons/histogram';
import { useFareItemProps } from '../_commons/tracking';

import prePopulationDefaultProps from '../_commons/prepopulationDefaultProps/histogram';
import HeaderContainer from '../../elements/HeaderContainer';

function HeroMonthlyHistogramContainerMobile(props) {
  const {
    prePopulation,
    integrationSettings,
    meta,
    pageContext,
    initialHistograms,
    initialLocation,
    initialOriginAirports,
    initialDestinationAirports,
    getHistograms,
    getAirports: getAirportsFn,
    maxItemsAmount,
    loading,
    showLoading,
    noResultsPlaceHolder,
    alert,
    disclaimer: disclaimerSettings,
    direction,
    fareItemOnClickAction,
    mainContentSettings,
    disclaimerContentSettings,
    showFilters,
    showNoResultsPlaceholder,
    minInitialResults,
    showSkeletonScreen,
    showDisclaimer,
    skeletonSettings,
    showHeroCard,
    heroCardSettings,
    theme,
    carouselSettings: carousel,
    filters: { availableFilters, hideBudgetOnMultiCurrency, ...filters },
    originDestinationLogic,
    enableSEO = true
  } = props;

  const [className, style] = useStyles(props, { direction, theme });
  const styles = Object.assign({ minHeight: '350px' }, style);

  const {
    fares: { match, ...fares },
    dispatch,
    tracking,
    retry,
    ...filtersState
  } = useFilters({
    originDestinationLogic,
    filter: {
      prePopulation,
      integrationSettings,
      meta,
      pageContext,
      getAirports: getAirportsFn,
      maxItemsAmount
    },
    initialData: initialHistograms
      ? { histograms: initialHistograms, location: initialLocation }
      : null,
    showNoResultsPlaceholder,
    minInitialResults,
    initialOrigins: initialOriginAirports,
    initialDestinations: initialDestinationAirports,
    getFares: getHistograms,
    faresTransformationLogic: 'histogram',
    availableFilters: showFilters ? availableFilters : [],
    hideBudgetOnMultiCurrency,
    routeMode: true,
    getResultsLength,
    showOrigin: filters.originDestinationSettings && filters.originDestinationSettings.showOrigin,
    showDestination:
      filters.originDestinationSettings && filters.originDestinationSettings.showDestination
  });

  const itemSettings = useFareItemProps(carousel.itemSettings, dispatch, fareItemOnClickAction);

  if (isSSR() && !Boolean(enableSEO)) {
    return null;
  }

  if (match('HIDDEN')) {
    return <NoFaresBehavior />;
  }

  function handleSeeMoreDeals() {
    tracking({ type: 'MORE_DEALS' });
    return true;
  }

  const header = (
    <>
      {showLoading && match('LOADING') && <Loading {...loading} />}
      {showSkeletonScreen && match('SKELETON') && (
        <Skeleton {...skeletonSettings} position="absolute" width="100" />
      )}
      <HeaderContainer
        defaultTitleValue={'Cheapest upcoming flights'}
        handleSeeMoreDeals={handleSeeMoreDeals}
        {...props}
      />
    </>
  );

  return (
    <InlineFilters
      className={`${className} ${HeroMonthlyHistogramContainerMobile.componentName}`}
      style={styles}
      header={header}
      dataTestId="heroMonthlyHistogramMobile"
      {...filters}
      {...filtersState}
    >
      <Row {...mainContentSettings} gutters>
        {match('ERROR') && (
          <Col>
            <ContainerErrorAlert {...alert} tryAgain={retry} />
          </Col>
        )}
        {match('WAITING_FOR_DESTINATION') && (
          <Col>
            <NextDestinationAlert {...noResultsPlaceHolder} />
          </Col>
        )}
        {match('NO_RESULTS') && (
          <Col>
            <NoFeaturedPopularFaresAlert noResultsPlaceHolder={noResultsPlaceHolder} />
          </Col>
        )}
        {match('COMPLETE') && (
          <>
            {showHeroCard && (
              <Col md="12">
                <FareItemCard {...heroCardSettings} data={fares.data.location} />
              </Col>
            )}
            <Col>
              <Carousel
                {...carousel}
                itemSettings={itemSettings}
                ItemComponentInstance={FareItemCard}
                itemsData={fares.data.histograms}
              />
            </Col>
          </>
        )}
      </Row>
      {showDisclaimer && (
        <Row {...disclaimerContentSettings}>
          <Col>
            <Disclaimer
              labelKey="cmp-hero-monthly-histogram-container-disclaimer"
              {...disclaimerSettings}
            />
          </Col>
        </Row>
      )}
    </InlineFilters>
  );
}

HeroMonthlyHistogramContainerMobile.propTypes = {
  /**
   * Props of alerts
   */
  alert: PropTypes.objectOf(PropTypes.any),
  /**
   * Props to carousel
   */
  carouselSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * To be used to set if the container is a controlled one
   */
  containerType: PropTypes.string,
  /**
   * Props to destination item
   */
  destinationItem: PropTypes.objectOf(PropTypes.any),
  /**
   * Direction of the component.
   */
  direction: PropTypes.oneOf(['rtl', 'ltr']),
  /**
   * Disclaimer Component
   */
  disclaimer: PropTypes.objectOf(PropTypes.any),
  /**
   * disclaimerContentSettings props
   */
  disclaimerContentSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * Filters settings
   */
  filters: PropTypes.objectOf(PropTypes.any),
  /**
   * Function for request to obtain airports
   */
  getAirports: PropTypes.func,
  /**
   * Function for request to obtain histograms
   */
  getHistograms: PropTypes.func,
  /**
   * headerContentSettings props
   */
  headerContentSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * HeroCardSettings props
   */
  heroCardSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * Used to prepopulate the component
   */
  initialOriginAirports: PropTypes.arrayOf(PropTypes.any),
  initialDestinationAirports: PropTypes.arrayOf(PropTypes.any),
  initialHistograms: PropTypes.arrayOf(PropTypes.any),
  initialLocation: PropTypes.objectOf(PropTypes.any),
  /**
   * All integration configurations
   */
  integrationSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * Loading Component
   */
  loading: PropTypes.objectOf(PropTypes.any),
  /**
   * Props to location selector
   */
  locationSelector: PropTypes.objectOf(PropTypes.any),
  /**
   * mainContentSettings props
   */
  mainContentSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * Number of month to show
   */
  maxItemsAmount: PropTypes.number,
  /**
   * Hide the component if the ammount of initial results is lower than this value
   */
  minInitialResults: PropTypes.number,
  /**
   * Props of noResultsPlaceHolder
   */
  noResultsPlaceHolder: PropTypes.objectOf(PropTypes.any),
  /**
   * pageContext settings
   */
  pageContext: PropTypes.objectOf(PropTypes.any),
  /**
   * Component PrePopulation
   */
  prePopulation: PropTypes.objectOf(PropTypes.any),
  /**
   * Show Airport Iata Code in locations
   */
  showAirportIataCode: PropTypes.bool,
  /**
   * Show disclaimer
   */
  showDisclaimer: PropTypes.bool,
  /**
   * Show filters
   */
  showFilters: PropTypes.bool,
  /**
   * Show hero card
   */
  showHeroCard: PropTypes.bool,
  /**
   * Determine if the component will show the skeleton screen while
   * loading (Default value will be false)
   */
  showSkeletonScreen: PropTypes.bool,
  /**
   * Shows Loading Component
   */
  showLoading: PropTypes.bool,
  /**
   * Show title
   */
  showTitle: PropTypes.bool,
  /**
   * Define if show noResultsPlaceHolder
   */
  showNoResultsPlaceholder: PropTypes.bool,
  /**
   * SkeletonSettings Component
   */
  skeletonSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * titleKey of the component
   */
  titleKey: PropTypes.string,
  /**
   * Title Component
   */
  titleSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * Props to translate
   */
  translate: PropTypes.func,
  /**
   * Prop used for ajusts year on the fares.
   */
  uiDatesYearsAdjustmentPatch: PropTypes.number,
  /**
   * Possible interactions will be Popup or Direct Deeplink
   */
  fareItemInteractionType: PropTypes.oneOf(['popup', 'deeplink']),
  /**
   * LocalizationData via moment to be used as a date translator
   * https://github.com/moment/moment/blob/develop/src/locale/es.js
   */
  localizationData: PropTypes.objectOf(PropTypes.any),
  /**
   * Action to be executed when clicking in fareItem
   */
  fareItemOnClickAction: PropTypes.func,
  /**
   * Parameters tracking
   */
  tracking: PropTypes.objectOf(PropTypes.any),
  /**
   * Theme object on the component
   */
  theme: PropTypes.objectOf(PropTypes.any),
  /**
   * All integration configurations
   */
  meta: PropTypes.objectOf(PropTypes.any),
  /**
   * Login to load origin and destination airports
   * same: Origin and Destination are pre-populated or not
   *      a single airport service call is done to fill out the Origin
   *      and Destinations filters (for performance purposes).
   * different: Origin and Destination are pre-populated or not
   *      2 airports service calls are done to fill out the Origin
   *      and Destinations filters.
   */
  originDestinationLogic: PropTypes.oneOf([
    OriginDestinationLogic.SAME,
    OriginDestinationLogic.DIFFERENT
  ]),
  /**
   * Show the SubTitle Component
   */
  showSubTitle: PropTypes.bool,
  /**
   * subHeaderContentSettings props
   */
  subHeaderContentSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * titleKey of the component
   */
  subTitleKey: PropTypes.string,
  /**
   * SubTitle Component
   */
  subTitleSettings: PropTypes.objectOf(PropTypes.any)
};
HeroMonthlyHistogramContainerMobile.defaultProps = {
  fareItemInteractionType: 'popup',
  alert: {
    bgColor: 'light',
    textAlign: 'center',
    padding: '3',
    rounded: '@theme:card.rounded',
    labelKeyTitle: 'cmp-container-error-message',
    labelKeySubTitle: 'cmp-container-error-reload-deals'
  },
  carouselSettings: {},
  containerType: 'stateful',
  destinationItem: {},
  direction: 'ltr',
  disclaimer: {},
  disclaimerContentSettings: {},
  filters: {
    availableFilters: ['OriginDestination', 'FlightType']
  },
  getAirports,
  getHistograms: getHistogramsByMonths,
  headerContentSettings: {},
  heroCardSettings: {},
  initialOriginAirports: null,
  initialDestinationAirports: null,
  initialHistograms: null,
  initialLocation: {},
  integrationSettings: {},
  loading: {},
  locationSelector: {},
  mainContentSettings: {
    marginTop: '2'
  },
  maxItemsAmount: 12,
  minInitialResults: null,
  noResultsPlaceHolder: {
    bgColor: 'light',
    rounded: '@theme:card.rounded'
  },
  pageContext: {
    deviceCategory: 'DESKTOP',
    siteEdition: 'en_HK'
  },
  prePopulation: prePopulationDefaultProps,
  showAirportIataCode: true,
  showDisclaimer: true,
  showFilters: true,
  showHeroCard: true,
  showSkeletonScreen: true,
  showLoading: true,
  showTitle: true,
  showNoResultsPlaceholder: false,
  skeletonSettings: {
    style: { backgroundColor: 'white' }
  },
  titleKey: 'cmp-hero-monthly-histogram-container-title',
  titleSettings: {},
  translate: () => {},
  uiDatesYearsAdjustmentPatch: null,
  localizationData: null,
  fareItemOnClickAction: null,
  tracking: {
    flexibleDates: false,
    passengerCount: 1,
    promoCode: 'EVERYMUNDO',
    eventType: 'heroMonthlyHistogramContainer'
  },
  theme: null,
  meta: null,
  originDestinationLogic: OriginDestinationLogic.SAME,
  showSubTitle: false,
  subHeaderContentSettings: {},
  subTitleKey: 'cmp-hero-monthly-histogram-sub-title',
  subTitleSettings: {}
};

HeroMonthlyHistogramContainerMobile.componentName = 'HeroMonthlyHistogramContainerMobile';

// HOC with common functionality to all containers
export default withCommonEMContainer(HeroMonthlyHistogramContainerMobile);
